import { supabase } from './supabase'
import type { OrgUnitOption } from './admin'
import type { PickerOption } from '../components/Picker'

/**
 * WHERE — one filter, three levels: Country → Legal entity → Business unit
 * (David, 9 Oct: "when filtering BU, it's in the LE order, let's change this
 * filter to where and have a multi layer Country-LE-BU").
 *
 * ── THERE IS NO COUNTRY ORG UNIT, AND THERE MUST NOT BE ONE FOR THIS ─────────
 *
 * `country` is not an `org_unit_type` and all 12 legal entities are roots, so
 * the tree's top level does not exist in the schema. It does not need to: every
 * active business unit carries `org_unit_business_unit.country` (measured 9 Oct,
 * dev and prod, no nulls), and an entity's country is DERIVED from its business
 * units — rule 8, a third fact from two others is not a column. No entity spans
 * two countries today; if one ever does, it simply appears under each, and the
 * country nodes still mean exactly their own business units.
 *
 * ── WHAT A CHOICE SENDS ──────────────────────────────────────────────────────
 *
 * The picker holds three kinds of value — `country:<name>`, an entity id, a BU
 * id — and `whereOrgUnitIds` turns them into org unit ids for the roster, where
 * the filter matches a unit or anything beneath it (044's cascade). A COUNTRY
 * becomes its entities — whole, so somebody placed at the entity itself still
 * matches — except an entity that also has units in another country, which is
 * narrowed to this country's units. Several choices are OR'd.
 *
 * Read with no limit (57 rows), so sorting here is standing rule 1's stated
 * exception: the caller already holds every row.
 */

export type BuPlace = { orgUnitId: string; country: string | null; city: string | null }

export async function loadBuPlaces(): Promise<BuPlace[]> {
  const { data, error } = await supabase.from('org_unit_business_unit').select('org_unit_id, country, city')
  if (error) throw error
  return ((data ?? []) as { org_unit_id: string; country: string | null; city: string | null }[]).map((r) => ({
    orgUnitId: r.org_unit_id,
    country: r.country,
    city: r.city,
  }))
}

const COUNTRY = 'country:'
/** People placed at an entity with no business unit under it still need a home. */
const NO_COUNTRY = 'No country recorded'

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

export type WhereTree = { country: string; entities: { le: OrgUnitOption; bus: OrgUnitOption[] }[] }[]
type Tree = WhereTree

export function whereTree(orgUnits: OrgUnitOption[], places: BuPlace[]): Tree {
  const countryOf = new Map(places.map((p) => [p.orgUnitId, p.country ?? null]))
  const bus = orgUnits.filter((o) => o.type === 'business_unit')
  const tree = new Map<string, Map<string, { le: OrgUnitOption; bus: OrgUnitOption[] }>>()
  const add = (country: string, le: OrgUnitOption) => {
    if (!tree.has(country)) tree.set(country, new Map())
    const les = tree.get(country)!
    if (!les.has(le.orgUnitId)) les.set(le.orgUnitId, { le, bus: [] })
    return les.get(le.orgUnitId)!
  }
  for (const le of orgUnits.filter((o) => o.type === 'legal_entity')) {
    const mine = bus.filter((b) => b.parentId === le.orgUnitId)
    const countries = [...new Set(mine.map((b) => countryOf.get(b.orgUnitId) ?? NO_COUNTRY))]
    if (countries.length === 0) countries.push(NO_COUNTRY)
    for (const c of countries) {
      const node = add(c, le)
      node.bus.push(...mine.filter((b) => (countryOf.get(b.orgUnitId) ?? NO_COUNTRY) === c))
    }
  }
  return [...tree.entries()]
    .sort(([a], [b]) => (a === NO_COUNTRY ? 1 : b === NO_COUNTRY ? -1 : byName(a, b)))
    .map(([country, les]) => ({
      country,
      entities: [...les.values()]
        .sort((a, b) => byName(a.le.name, b.le.name))
        .map((e) => ({ le: e.le, bus: [...e.bus].sort((a, b) => byName(a.name, b.name)) })),
    }))
}

/** The picker's rows, in tree order, each naming its ancestors so a child of a chosen node reads as covered. */
export function whereOptions(tree: Tree): PickerOption[] {
  const out: PickerOption[] = []
  for (const c of tree) {
    const cv = COUNTRY + c.country
    out.push({ value: cv, label: c.country, depth: 0, hint: 'Country' })
    for (const { le, bus } of c.entities) {
      out.push({ value: le.orgUnitId, label: le.name, depth: 1, hint: c.country, ancestors: [cv] })
      for (const bu of bus) {
        out.push({ value: bu.orgUnitId, label: bu.name, depth: 2, hint: `${c.country} › ${le.name}`, ancestors: [cv, le.orgUnitId] })
      }
    }
  }
  return out
}

/** What the roster is sent: org unit ids, a country expanded to its business units. */
export function whereOrgUnitIds(selected: string[], tree: Tree): string[] {
  const ids = new Set<string>()
  for (const v of selected) {
    if (v.startsWith(COUNTRY)) {
      const c = tree.find((t) => t.country === v.slice(COUNTRY.length))
      for (const e of c?.entities ?? []) {
        // An entity wholly inside this country is sent WHOLE, so people placed
        // at the entity itself (the build team sit there) still match. Only an
        // entity that also has units elsewhere is narrowed to this country's.
        const elsewhere = tree.some((t) => t.country !== c!.country && t.entities.some((x) => x.le.orgUnitId === e.le.orgUnitId))
        if (!elsewhere) ids.add(e.le.orgUnitId)
        else for (const bu of e.bus) ids.add(bu.orgUnitId)
      }
    } else ids.add(v)
  }
  return [...ids]
}

/** A chip's label: the country, the entity, or the BU with nothing else needed (its name carries its code). */
export function whereLabel(value: string, options: PickerOption[]): string {
  return options.find((o) => o.value === value)?.label ?? value.replace(COUNTRY, '')
}

/* ── 159: the same tree as a SCOPE picker ───────────────────────────────────

   A scoped grant names org units, one role_assignment each (153: a region is a
   row per legal entity or business unit). Choosing it from the Where tree lets
   "Canada" be one choice: whereOrgUnitIds turns the country into its entities
   on the day of the grant, which the picker SAYS — an entity added next year is
   not in a grant made today, and there is no country org unit to make it so.

   `units` is what the caller may name for this role (scopeOptionsFor: the
   role's scope types, a regional holder's region). Business units whose entity
   is not among them (a region that is one BU) still need a row, and so do
   departments when the role may name one. */

export function scopeOptions(units: OrgUnitOption[], places: BuPlace[]): { options: PickerOption[]; tree: WhereTree } {
  const tree = whereTree(units, places)
  const options = whereOptions(tree)
  const shown = new Set(options.map((o) => o.value))
  const byId = new Map(units.map((u) => [u.orgUnitId, u]))
  for (const u of [...units].sort((a, b) => byName(a.name, b.name))) {
    if (u.type === 'business_unit' && !shown.has(u.orgUnitId)) {
      options.push({ value: u.orgUnitId, label: u.name, depth: 0, hint: byId.get(u.parentId ?? '')?.name ?? 'Business unit' })
    }
  }
  for (const u of [...units].filter((x) => x.type === 'department').sort((a, b) => byName(a.name, b.name))) {
    options.push({ value: u.orgUnitId, label: u.name, depth: 0, hint: 'Department' })
  }
  return { options, tree }
}

/** The org units a scope choice grants for: a country becomes its entities (whereOrgUnitIds). */
export function scopeIds(selected: string[], tree: WhereTree): string[] {
  return whereOrgUnitIds(selected, tree)
}

/** True when a choice is a country, so the page can say what that means. */
export function isCountryChoice(value: string): boolean {
  return value.startsWith(COUNTRY)
}

/** "Canada + 51 (ConeTec - US - Corporate)": the choices in words, as chosen. */
export function scopeWords(selected: string[], options: PickerOption[]): string {
  return selected.map((v) => whereLabel(v, options)).join(' + ')
}
